/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const q = globalThis, ee = q.ShadowRoot && (q.ShadyCSS === void 0 || q.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, te = Symbol(), de = /* @__PURE__ */ new WeakMap();
let $e = class {
  constructor(e, t, i) {
    if (this._$cssResult$ = !0, i !== te) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = e, this.t = t;
  }
  get styleSheet() {
    let e = this.o;
    const t = this.t;
    if (ee && e === void 0) {
      const i = t !== void 0 && t.length === 1;
      i && (e = de.get(t)), e === void 0 && ((this.o = e = new CSSStyleSheet()).replaceSync(this.cssText), i && de.set(t, e));
    }
    return e;
  }
  toString() {
    return this.cssText;
  }
};
const Ae = (n) => new $e(typeof n == "string" ? n : n + "", void 0, te), ie = (n, ...e) => {
  const t = n.length === 1 ? n[0] : e.reduce((i, r, s) => i + ((o) => {
    if (o._$cssResult$ === !0) return o.cssText;
    if (typeof o == "number") return o;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + o + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(r) + n[s + 1], n[0]);
  return new $e(t, n, te);
}, ze = (n, e) => {
  if (ee) n.adoptedStyleSheets = e.map((t) => t instanceof CSSStyleSheet ? t : t.styleSheet);
  else for (const t of e) {
    const i = document.createElement("style"), r = q.litNonce;
    r !== void 0 && i.setAttribute("nonce", r), i.textContent = t.cssText, n.appendChild(i);
  }
}, he = ee ? (n) => n : (n) => n instanceof CSSStyleSheet ? ((e) => {
  let t = "";
  for (const i of e.cssRules) t += i.cssText;
  return Ae(t);
})(n) : n;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: Ie, defineProperty: He, getOwnPropertyDescriptor: Be, getOwnPropertyNames: Me, getOwnPropertySymbols: Te, getPrototypeOf: Ne } = Object, w = globalThis, ue = w.trustedTypes, Ue = ue ? ue.emptyScript : "", J = w.reactiveElementPolyfillSupport, I = (n, e) => n, W = { toAttribute(n, e) {
  switch (e) {
    case Boolean:
      n = n ? Ue : null;
      break;
    case Object:
    case Array:
      n = n == null ? n : JSON.stringify(n);
  }
  return n;
}, fromAttribute(n, e) {
  let t = n;
  switch (e) {
    case Boolean:
      t = n !== null;
      break;
    case Number:
      t = n === null ? null : Number(n);
      break;
    case Object:
    case Array:
      try {
        t = JSON.parse(n);
      } catch {
        t = null;
      }
  }
  return t;
} }, re = (n, e) => !Ie(n, e), pe = { attribute: !0, type: String, converter: W, reflect: !1, useDefault: !1, hasChanged: re };
Symbol.metadata ?? (Symbol.metadata = Symbol("metadata")), w.litPropertyMetadata ?? (w.litPropertyMetadata = /* @__PURE__ */ new WeakMap());
let S = class extends HTMLElement {
  static addInitializer(e) {
    this._$Ei(), (this.l ?? (this.l = [])).push(e);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(e, t = pe) {
    if (t.state && (t.attribute = !1), this._$Ei(), this.prototype.hasOwnProperty(e) && ((t = Object.create(t)).wrapped = !0), this.elementProperties.set(e, t), !t.noAccessor) {
      const i = Symbol(), r = this.getPropertyDescriptor(e, i, t);
      r !== void 0 && He(this.prototype, e, r);
    }
  }
  static getPropertyDescriptor(e, t, i) {
    const { get: r, set: s } = Be(this.prototype, e) ?? { get() {
      return this[t];
    }, set(o) {
      this[t] = o;
    } };
    return { get: r, set(o) {
      const l = r == null ? void 0 : r.call(this);
      s == null || s.call(this, o), this.requestUpdate(e, l, i);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(e) {
    return this.elementProperties.get(e) ?? pe;
  }
  static _$Ei() {
    if (this.hasOwnProperty(I("elementProperties"))) return;
    const e = Ne(this);
    e.finalize(), e.l !== void 0 && (this.l = [...e.l]), this.elementProperties = new Map(e.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(I("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(I("properties"))) {
      const t = this.properties, i = [...Me(t), ...Te(t)];
      for (const r of i) this.createProperty(r, t[r]);
    }
    const e = this[Symbol.metadata];
    if (e !== null) {
      const t = litPropertyMetadata.get(e);
      if (t !== void 0) for (const [i, r] of t) this.elementProperties.set(i, r);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [t, i] of this.elementProperties) {
      const r = this._$Eu(t, i);
      r !== void 0 && this._$Eh.set(r, t);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(e) {
    const t = [];
    if (Array.isArray(e)) {
      const i = new Set(e.flat(1 / 0).reverse());
      for (const r of i) t.unshift(he(r));
    } else e !== void 0 && t.push(he(e));
    return t;
  }
  static _$Eu(e, t) {
    const i = t.attribute;
    return i === !1 ? void 0 : typeof i == "string" ? i : typeof e == "string" ? e.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    var e;
    this._$ES = new Promise((t) => this.enableUpdating = t), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), (e = this.constructor.l) == null || e.forEach((t) => t(this));
  }
  addController(e) {
    var t;
    (this._$EO ?? (this._$EO = /* @__PURE__ */ new Set())).add(e), this.renderRoot !== void 0 && this.isConnected && ((t = e.hostConnected) == null || t.call(e));
  }
  removeController(e) {
    var t;
    (t = this._$EO) == null || t.delete(e);
  }
  _$E_() {
    const e = /* @__PURE__ */ new Map(), t = this.constructor.elementProperties;
    for (const i of t.keys()) this.hasOwnProperty(i) && (e.set(i, this[i]), delete this[i]);
    e.size > 0 && (this._$Ep = e);
  }
  createRenderRoot() {
    const e = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return ze(e, this.constructor.elementStyles), e;
  }
  connectedCallback() {
    var e;
    this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (e = this._$EO) == null || e.forEach((t) => {
      var i;
      return (i = t.hostConnected) == null ? void 0 : i.call(t);
    });
  }
  enableUpdating(e) {
  }
  disconnectedCallback() {
    var e;
    (e = this._$EO) == null || e.forEach((t) => {
      var i;
      return (i = t.hostDisconnected) == null ? void 0 : i.call(t);
    });
  }
  attributeChangedCallback(e, t, i) {
    this._$AK(e, i);
  }
  _$ET(e, t) {
    var s;
    const i = this.constructor.elementProperties.get(e), r = this.constructor._$Eu(e, i);
    if (r !== void 0 && i.reflect === !0) {
      const o = (((s = i.converter) == null ? void 0 : s.toAttribute) !== void 0 ? i.converter : W).toAttribute(t, i.type);
      this._$Em = e, o == null ? this.removeAttribute(r) : this.setAttribute(r, o), this._$Em = null;
    }
  }
  _$AK(e, t) {
    var s, o;
    const i = this.constructor, r = i._$Eh.get(e);
    if (r !== void 0 && this._$Em !== r) {
      const l = i.getPropertyOptions(r), a = typeof l.converter == "function" ? { fromAttribute: l.converter } : ((s = l.converter) == null ? void 0 : s.fromAttribute) !== void 0 ? l.converter : W;
      this._$Em = r, this[r] = a.fromAttribute(t, l.type) ?? ((o = this._$Ej) == null ? void 0 : o.get(r)) ?? null, this._$Em = null;
    }
  }
  requestUpdate(e, t, i) {
    var r;
    if (e !== void 0) {
      const s = this.constructor, o = this[e];
      if (i ?? (i = s.getPropertyOptions(e)), !((i.hasChanged ?? re)(o, t) || i.useDefault && i.reflect && o === ((r = this._$Ej) == null ? void 0 : r.get(e)) && !this.hasAttribute(s._$Eu(e, i)))) return;
      this.C(e, t, i);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$EP());
  }
  C(e, t, { useDefault: i, reflect: r, wrapped: s }, o) {
    i && !(this._$Ej ?? (this._$Ej = /* @__PURE__ */ new Map())).has(e) && (this._$Ej.set(e, o ?? t ?? this[e]), s !== !0 || o !== void 0) || (this._$AL.has(e) || (this.hasUpdated || i || (t = void 0), this._$AL.set(e, t)), r === !0 && this._$Em !== e && (this._$Eq ?? (this._$Eq = /* @__PURE__ */ new Set())).add(e));
  }
  async _$EP() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (t) {
      Promise.reject(t);
    }
    const e = this.scheduleUpdate();
    return e != null && await e, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var i;
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this._$Ep) {
        for (const [s, o] of this._$Ep) this[s] = o;
        this._$Ep = void 0;
      }
      const r = this.constructor.elementProperties;
      if (r.size > 0) for (const [s, o] of r) {
        const { wrapped: l } = o, a = this[s];
        l !== !0 || this._$AL.has(s) || a === void 0 || this.C(s, void 0, o, a);
      }
    }
    let e = !1;
    const t = this._$AL;
    try {
      e = this.shouldUpdate(t), e ? (this.willUpdate(t), (i = this._$EO) == null || i.forEach((r) => {
        var s;
        return (s = r.hostUpdate) == null ? void 0 : s.call(r);
      }), this.update(t)) : this._$EM();
    } catch (r) {
      throw e = !1, this._$EM(), r;
    }
    e && this._$AE(t);
  }
  willUpdate(e) {
  }
  _$AE(e) {
    var t;
    (t = this._$EO) == null || t.forEach((i) => {
      var r;
      return (r = i.hostUpdated) == null ? void 0 : r.call(i);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(e)), this.updated(e);
  }
  _$EM() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(e) {
    return !0;
  }
  update(e) {
    this._$Eq && (this._$Eq = this._$Eq.forEach((t) => this._$ET(t, this[t]))), this._$EM();
  }
  updated(e) {
  }
  firstUpdated(e) {
  }
};
S.elementStyles = [], S.shadowRootOptions = { mode: "open" }, S[I("elementProperties")] = /* @__PURE__ */ new Map(), S[I("finalized")] = /* @__PURE__ */ new Map(), J == null || J({ ReactiveElement: S }), (w.reactiveElementVersions ?? (w.reactiveElementVersions = [])).push("2.1.0");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const H = globalThis, G = H.trustedTypes, me = G ? G.createPolicy("lit-html", { createHTML: (n) => n }) : void 0, _e = "$lit$", v = `lit$${Math.random().toFixed(9).slice(2)}$`, Ee = "?" + v, Re = `<${Ee}>`, E = document, M = () => E.createComment(""), T = (n) => n === null || typeof n != "object" && typeof n != "function", ne = Array.isArray, Le = (n) => ne(n) || typeof (n == null ? void 0 : n[Symbol.iterator]) == "function", K = `[ 	
\f\r]`, z = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, fe = /-->/g, ge = />/g, $ = RegExp(`>|${K}(?:([^\\s"'>=/]+)(${K}*=${K}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), be = /'/g, ye = /"/g, Se = /^(?:script|style|textarea|title)$/i, De = (n) => (e, ...t) => ({ _$litType$: n, strings: e, values: t }), V = De(1), k = Symbol.for("lit-noChange"), h = Symbol.for("lit-nothing"), ve = /* @__PURE__ */ new WeakMap(), A = E.createTreeWalker(E, 129);
function ke(n, e) {
  if (!ne(n) || !n.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return me !== void 0 ? me.createHTML(e) : e;
}
const je = (n, e) => {
  const t = n.length - 1, i = [];
  let r, s = e === 2 ? "<svg>" : e === 3 ? "<math>" : "", o = z;
  for (let l = 0; l < t; l++) {
    const a = n[l];
    let c, u, d = -1, g = 0;
    for (; g < a.length && (o.lastIndex = g, u = o.exec(a), u !== null); ) g = o.lastIndex, o === z ? u[1] === "!--" ? o = fe : u[1] !== void 0 ? o = ge : u[2] !== void 0 ? (Se.test(u[2]) && (r = RegExp("</" + u[2], "g")), o = $) : u[3] !== void 0 && (o = $) : o === $ ? u[0] === ">" ? (o = r ?? z, d = -1) : u[1] === void 0 ? d = -2 : (d = o.lastIndex - u[2].length, c = u[1], o = u[3] === void 0 ? $ : u[3] === '"' ? ye : be) : o === ye || o === be ? o = $ : o === fe || o === ge ? o = z : (o = $, r = void 0);
    const y = o === $ && n[l + 1].startsWith("/>") ? " " : "";
    s += o === z ? a + Re : d >= 0 ? (i.push(c), a.slice(0, d) + _e + a.slice(d) + v + y) : a + v + (d === -2 ? l : y);
  }
  return [ke(n, s + (n[t] || "<?>") + (e === 2 ? "</svg>" : e === 3 ? "</math>" : "")), i];
};
class N {
  constructor({ strings: e, _$litType$: t }, i) {
    let r;
    this.parts = [];
    let s = 0, o = 0;
    const l = e.length - 1, a = this.parts, [c, u] = je(e, t);
    if (this.el = N.createElement(c, i), A.currentNode = this.el.content, t === 2 || t === 3) {
      const d = this.el.content.firstChild;
      d.replaceWith(...d.childNodes);
    }
    for (; (r = A.nextNode()) !== null && a.length < l; ) {
      if (r.nodeType === 1) {
        if (r.hasAttributes()) for (const d of r.getAttributeNames()) if (d.endsWith(_e)) {
          const g = u[o++], y = r.getAttribute(d).split(v), j = /([.?@])?(.*)/.exec(g);
          a.push({ type: 1, index: s, name: j[2], strings: y, ctor: j[1] === "." ? We : j[1] === "?" ? Ge : j[1] === "@" ? Fe : Z }), r.removeAttribute(d);
        } else d.startsWith(v) && (a.push({ type: 6, index: s }), r.removeAttribute(d));
        if (Se.test(r.tagName)) {
          const d = r.textContent.split(v), g = d.length - 1;
          if (g > 0) {
            r.textContent = G ? G.emptyScript : "";
            for (let y = 0; y < g; y++) r.append(d[y], M()), A.nextNode(), a.push({ type: 2, index: ++s });
            r.append(d[g], M());
          }
        }
      } else if (r.nodeType === 8) if (r.data === Ee) a.push({ type: 2, index: s });
      else {
        let d = -1;
        for (; (d = r.data.indexOf(v, d + 1)) !== -1; ) a.push({ type: 7, index: s }), d += v.length - 1;
      }
      s++;
    }
  }
  static createElement(e, t) {
    const i = E.createElement("template");
    return i.innerHTML = e, i;
  }
}
function C(n, e, t = n, i) {
  var o, l;
  if (e === k) return e;
  let r = i !== void 0 ? (o = t._$Co) == null ? void 0 : o[i] : t._$Cl;
  const s = T(e) ? void 0 : e._$litDirective$;
  return (r == null ? void 0 : r.constructor) !== s && ((l = r == null ? void 0 : r._$AO) == null || l.call(r, !1), s === void 0 ? r = void 0 : (r = new s(n), r._$AT(n, t, i)), i !== void 0 ? (t._$Co ?? (t._$Co = []))[i] = r : t._$Cl = r), r !== void 0 && (e = C(n, r._$AS(n, e.values), r, i)), e;
}
class qe {
  constructor(e, t) {
    this._$AV = [], this._$AN = void 0, this._$AD = e, this._$AM = t;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(e) {
    const { el: { content: t }, parts: i } = this._$AD, r = ((e == null ? void 0 : e.creationScope) ?? E).importNode(t, !0);
    A.currentNode = r;
    let s = A.nextNode(), o = 0, l = 0, a = i[0];
    for (; a !== void 0; ) {
      if (o === a.index) {
        let c;
        a.type === 2 ? c = new L(s, s.nextSibling, this, e) : a.type === 1 ? c = new a.ctor(s, a.name, a.strings, this, e) : a.type === 6 && (c = new Ve(s, this, e)), this._$AV.push(c), a = i[++l];
      }
      o !== (a == null ? void 0 : a.index) && (s = A.nextNode(), o++);
    }
    return A.currentNode = E, r;
  }
  p(e) {
    let t = 0;
    for (const i of this._$AV) i !== void 0 && (i.strings !== void 0 ? (i._$AI(e, i, t), t += i.strings.length - 2) : i._$AI(e[t])), t++;
  }
}
class L {
  get _$AU() {
    var e;
    return ((e = this._$AM) == null ? void 0 : e._$AU) ?? this._$Cv;
  }
  constructor(e, t, i, r) {
    this.type = 2, this._$AH = h, this._$AN = void 0, this._$AA = e, this._$AB = t, this._$AM = i, this.options = r, this._$Cv = (r == null ? void 0 : r.isConnected) ?? !0;
  }
  get parentNode() {
    let e = this._$AA.parentNode;
    const t = this._$AM;
    return t !== void 0 && (e == null ? void 0 : e.nodeType) === 11 && (e = t.parentNode), e;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(e, t = this) {
    e = C(this, e, t), T(e) ? e === h || e == null || e === "" ? (this._$AH !== h && this._$AR(), this._$AH = h) : e !== this._$AH && e !== k && this._(e) : e._$litType$ !== void 0 ? this.$(e) : e.nodeType !== void 0 ? this.T(e) : Le(e) ? this.k(e) : this._(e);
  }
  O(e) {
    return this._$AA.parentNode.insertBefore(e, this._$AB);
  }
  T(e) {
    this._$AH !== e && (this._$AR(), this._$AH = this.O(e));
  }
  _(e) {
    this._$AH !== h && T(this._$AH) ? this._$AA.nextSibling.data = e : this.T(E.createTextNode(e)), this._$AH = e;
  }
  $(e) {
    var s;
    const { values: t, _$litType$: i } = e, r = typeof i == "number" ? this._$AC(e) : (i.el === void 0 && (i.el = N.createElement(ke(i.h, i.h[0]), this.options)), i);
    if (((s = this._$AH) == null ? void 0 : s._$AD) === r) this._$AH.p(t);
    else {
      const o = new qe(r, this), l = o.u(this.options);
      o.p(t), this.T(l), this._$AH = o;
    }
  }
  _$AC(e) {
    let t = ve.get(e.strings);
    return t === void 0 && ve.set(e.strings, t = new N(e)), t;
  }
  k(e) {
    ne(this._$AH) || (this._$AH = [], this._$AR());
    const t = this._$AH;
    let i, r = 0;
    for (const s of e) r === t.length ? t.push(i = new L(this.O(M()), this.O(M()), this, this.options)) : i = t[r], i._$AI(s), r++;
    r < t.length && (this._$AR(i && i._$AB.nextSibling, r), t.length = r);
  }
  _$AR(e = this._$AA.nextSibling, t) {
    var i;
    for ((i = this._$AP) == null ? void 0 : i.call(this, !1, !0, t); e && e !== this._$AB; ) {
      const r = e.nextSibling;
      e.remove(), e = r;
    }
  }
  setConnected(e) {
    var t;
    this._$AM === void 0 && (this._$Cv = e, (t = this._$AP) == null || t.call(this, e));
  }
}
class Z {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(e, t, i, r, s) {
    this.type = 1, this._$AH = h, this._$AN = void 0, this.element = e, this.name = t, this._$AM = r, this.options = s, i.length > 2 || i[0] !== "" || i[1] !== "" ? (this._$AH = Array(i.length - 1).fill(new String()), this.strings = i) : this._$AH = h;
  }
  _$AI(e, t = this, i, r) {
    const s = this.strings;
    let o = !1;
    if (s === void 0) e = C(this, e, t, 0), o = !T(e) || e !== this._$AH && e !== k, o && (this._$AH = e);
    else {
      const l = e;
      let a, c;
      for (e = s[0], a = 0; a < s.length - 1; a++) c = C(this, l[i + a], t, a), c === k && (c = this._$AH[a]), o || (o = !T(c) || c !== this._$AH[a]), c === h ? e = h : e !== h && (e += (c ?? "") + s[a + 1]), this._$AH[a] = c;
    }
    o && !r && this.j(e);
  }
  j(e) {
    e === h ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, e ?? "");
  }
}
class We extends Z {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(e) {
    this.element[this.name] = e === h ? void 0 : e;
  }
}
class Ge extends Z {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(e) {
    this.element.toggleAttribute(this.name, !!e && e !== h);
  }
}
class Fe extends Z {
  constructor(e, t, i, r, s) {
    super(e, t, i, r, s), this.type = 5;
  }
  _$AI(e, t = this) {
    if ((e = C(this, e, t, 0) ?? h) === k) return;
    const i = this._$AH, r = e === h && i !== h || e.capture !== i.capture || e.once !== i.once || e.passive !== i.passive, s = e !== h && (i === h || r);
    r && this.element.removeEventListener(this.name, this, i), s && this.element.addEventListener(this.name, this, e), this._$AH = e;
  }
  handleEvent(e) {
    var t;
    typeof this._$AH == "function" ? this._$AH.call(((t = this.options) == null ? void 0 : t.host) ?? this.element, e) : this._$AH.handleEvent(e);
  }
}
class Ve {
  constructor(e, t, i) {
    this.element = e, this.type = 6, this._$AN = void 0, this._$AM = t, this.options = i;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(e) {
    C(this, e);
  }
}
const Q = H.litHtmlPolyfillSupport;
Q == null || Q(N, L), (H.litHtmlVersions ?? (H.litHtmlVersions = [])).push("3.3.0");
const Ze = (n, e, t) => {
  const i = (t == null ? void 0 : t.renderBefore) ?? e;
  let r = i._$litPart$;
  if (r === void 0) {
    const s = (t == null ? void 0 : t.renderBefore) ?? null;
    i._$litPart$ = r = new L(e.insertBefore(M(), s), s, void 0, t ?? {});
  }
  return r._$AI(n), r;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const _ = globalThis;
let x = class extends S {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var t;
    const e = super.createRenderRoot();
    return (t = this.renderOptions).renderBefore ?? (t.renderBefore = e.firstChild), e;
  }
  update(e) {
    const t = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(e), this._$Do = Ze(t, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var e;
    super.connectedCallback(), (e = this._$Do) == null || e.setConnected(!0);
  }
  disconnectedCallback() {
    var e;
    super.disconnectedCallback(), (e = this._$Do) == null || e.setConnected(!1);
  }
  render() {
    return k;
  }
};
var xe;
x._$litElement$ = !0, x.finalized = !0, (xe = _.litElementHydrateSupport) == null || xe.call(_, { LitElement: x });
const X = _.litElementPolyfillSupport;
X == null || X({ LitElement: x });
(_.litElementVersions ?? (_.litElementVersions = [])).push("4.2.0");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Je = { attribute: !0, type: String, converter: W, reflect: !1, hasChanged: re }, Ke = (n = Je, e, t) => {
  const { kind: i, metadata: r } = t;
  let s = globalThis.litPropertyMetadata.get(r);
  if (s === void 0 && globalThis.litPropertyMetadata.set(r, s = /* @__PURE__ */ new Map()), i === "setter" && ((n = Object.create(n)).wrapped = !0), s.set(t.name, n), i === "accessor") {
    const { name: o } = t;
    return { set(l) {
      const a = e.get.call(this);
      e.set.call(this, l), this.requestUpdate(o, a, n);
    }, init(l) {
      return l !== void 0 && this.C(o, void 0, n, l), l;
    } };
  }
  if (i === "setter") {
    const { name: o } = t;
    return function(l) {
      const a = this[o];
      e.call(this, l), this.requestUpdate(o, a, n);
    };
  }
  throw Error("Unsupported decorator location: " + i);
};
function f(n) {
  return (e, t) => typeof t == "object" ? Ke(n, e, t) : ((i, r, s) => {
    const o = r.hasOwnProperty(s);
    return r.constructor.createProperty(s, i), o ? Object.getOwnPropertyDescriptor(r, s) : void 0;
  })(n, e, t);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function b(n) {
  return f({ ...n, state: !0, attribute: !1 });
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Ce = (n, e, t) => (t.configurable = !0, t.enumerable = !0, Reflect.decorate && typeof e != "object" && Object.defineProperty(n, e, t), t);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function Oe(n, e) {
  return (t, i, r) => {
    const s = (o) => {
      var l;
      return ((l = o.renderRoot) == null ? void 0 : l.querySelector(n)) ?? null;
    };
    return Ce(t, i, { get() {
      return s(this);
    } });
  };
}
/**
 * @license
 * Copyright 2021 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function D(n) {
  return (e, t) => {
    const { slot: i, selector: r } = n ?? {}, s = "slot" + (i ? `[name=${i}]` : ":not([name])");
    return Ce(e, t, { get() {
      var a;
      const o = (a = this.renderRoot) == null ? void 0 : a.querySelector(s), l = (o == null ? void 0 : o.assignedElements(n)) ?? [];
      return r === void 0 ? l : l.filter((c) => c.matches(r));
    } });
  };
}
var Qe = Object.defineProperty, O = (n, e, t, i) => {
  for (var r = void 0, s = n.length - 1, o; s >= 0; s--)
    (o = n[s]) && (r = o(e, t, r) || r);
  return r && Qe(e, t, r), r;
};
class P extends x {
  constructor() {
    super(...arguments), this.carouselSelector = "nui-carousel", this.navigationNextSelector = "nui-carousel-navigation-next", this.navigationPrevSelector = "nui-carousel-navigation-prev", this.prefix = "nui-carousel";
  }
  connectedCallback() {
    super.connectedCallback(), this.addEventListener("carousel:next-arrow-clicked", (e) => {
      e.stopImmediatePropagation(), this.slottedBody.forEach((t) => {
        const i = t.querySelector(
          this.carouselSelector
        );
        i && (i.next(), this.dispatchEvent(
          new CustomEvent(`${this.prefix}:next-action`, {
            bubbles: !0
          })
        ));
      });
    }), this.addEventListener("carousel:prev-arrow-clicked", (e) => {
      e.stopImmediatePropagation(), this.slottedBody.forEach((t) => {
        const i = t.querySelector(
          this.carouselSelector
        );
        i && (i.prev(), this.dispatchEvent(
          new CustomEvent(`${this.prefix}:prev-action`, {
            bubbles: !0
          })
        ));
      });
    }), this.addEventListener("carousel:last-slide-visible", (e) => {
      e.stopImmediatePropagation(), this.slottedHeader.forEach((t) => {
        const i = t.querySelector(
          this.navigationNextSelector
        );
        i && i.setAttribute("is-disabled", "true");
      });
    }), this.addEventListener("carousel:last-slide-not-visible", (e) => {
      e.stopImmediatePropagation(), this.slottedHeader.forEach((t) => {
        const i = t.querySelector(
          this.navigationNextSelector
        );
        i && i.removeAttribute("is-disabled");
      });
    }), this.addEventListener("carousel:first-slide-visible", (e) => {
      e.stopImmediatePropagation(), this.slottedHeader.forEach((t) => {
        const i = t.querySelector(
          this.navigationPrevSelector
        );
        i && i.setAttribute("is-disabled", "true");
      });
    }), this.addEventListener("carousel:first-slide-not-visible", (e) => {
      e.stopImmediatePropagation(), this.slottedHeader.forEach((t) => {
        const i = t.querySelector(
          this.navigationPrevSelector
        );
        i && i.removeAttribute("is-disabled");
      });
    });
  }
  render() {
    return V`
            <div>
                <slot name="header"></slot>
                <slot name="body"></slot>
            </div>
        `;
  }
}
O([
  f({ type: String, reflect: !0, attribute: "carousel-selector" })
], P.prototype, "carouselSelector");
O([
  f({
    type: String,
    reflect: !0,
    attribute: "navigation-next-selector"
  })
], P.prototype, "navigationNextSelector");
O([
  f({
    type: String,
    reflect: !0,
    attribute: "navigation-prev-selector"
  })
], P.prototype, "navigationPrevSelector");
O([
  f({ type: String, reflect: !0, attribute: "event-prefix" })
], P.prototype, "prefix");
O([
  D({
    selector: "*",
    flatten: !0,
    slot: "header"
  })
], P.prototype, "slottedHeader");
O([
  D({
    selector: "*",
    flatten: !0,
    slot: "body"
  })
], P.prototype, "slottedBody");
var Xe = Object.defineProperty, se = (n, e, t, i) => {
  for (var r = void 0, s = n.length - 1, o; s >= 0; s--)
    (o = n[s]) && (r = o(e, t, r) || r);
  return r && Xe(e, t, r), r;
};
const ae = class ae extends x {
  constructor() {
    super(...arguments), this.prefix = "carousel";
  }
  attributeChangedCallback(e, t, i) {
    super.attributeChangedCallback(e, t, i), e === "is-disabled" && (this.isDisabled ? this.slottedNext.forEach((r) => {
      r.setAttribute("disabled", "true");
    }) : this.slottedNext.forEach((r) => {
      r.removeAttribute("disabled");
    }));
  }
  firstUpdated() {
    this.slottedNext.forEach((e) => {
      e.addEventListener("click", () => {
        const t = new CustomEvent(
          "carousel:prev-arrow-clicked",
          {
            bubbles: !0
          }
        );
        this.dispatchEvent(t);
      });
    });
  }
  render() {
    return V` <slot></slot> `;
  }
};
ae.styles = ie`
        :host { 
            display: contents 
            }
        }
    `;
let U = ae;
se([
  f({ type: String, reflect: !0, attribute: "event-prefix" })
], U.prototype, "prefix");
se([
  f({ type: Boolean, reflect: !0, attribute: "is-disabled" })
], U.prototype, "isDisabled");
se([
  D({
    selector: "*",
    flatten: !0,
    slot: ""
  })
], U.prototype, "slottedNext");
var Ye = Object.defineProperty, oe = (n, e, t, i) => {
  for (var r = void 0, s = n.length - 1, o; s >= 0; s--)
    (o = n[s]) && (r = o(e, t, r) || r);
  return r && Ye(e, t, r), r;
};
const le = class le extends x {
  constructor() {
    super(...arguments), this.prefix = "carousel";
  }
  attributeChangedCallback(e, t, i) {
    super.attributeChangedCallback(e, t, i), e === "is-disabled" && (this.isDisabled ? this.slottedNext.forEach((r) => {
      r.setAttribute("disabled", "true");
    }) : this.slottedNext.forEach((r) => {
      r.removeAttribute("disabled");
    }));
  }
  /**
   * @todo maybe add only one event listener to the top most element
   */
  firstUpdated() {
    this.slottedNext.forEach((e) => {
      e.addEventListener("click", () => {
        const t = new CustomEvent(
          `${this.prefix}:next-arrow-clicked`,
          {
            bubbles: !0
          }
        );
        this.dispatchEvent(t);
      });
    });
  }
  render() {
    return V` <slot></slot> `;
  }
};
le.styles = ie`
        :host {
            display: contents;
        }
    `;
let R = le;
oe([
  f({ type: String, reflect: !0, attribute: "event-prefix" })
], R.prototype, "prefix");
oe([
  f({ type: Boolean, reflect: !0, attribute: "is-disabled" })
], R.prototype, "isDisabled");
oe([
  D({
    selector: "*",
    flatten: !0,
    slot: ""
  })
], R.prototype, "slottedNext");
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const et = (n) => n.strings === void 0;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const tt = { CHILD: 2 }, it = (n) => (...e) => ({ _$litDirective$: n, values: e });
class rt {
  constructor(e) {
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AT(e, t, i) {
    this._$Ct = e, this._$AM = t, this._$Ci = i;
  }
  _$AS(e, t) {
    return this.update(e, t);
  }
  update(e, t) {
    return this.render(...t);
  }
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const B = (n, e) => {
  var i;
  const t = n._$AN;
  if (t === void 0) return !1;
  for (const r of t) (i = r._$AO) == null || i.call(r, e, !1), B(r, e);
  return !0;
}, F = (n) => {
  let e, t;
  do {
    if ((e = n._$AM) === void 0) break;
    t = e._$AN, t.delete(n), n = e;
  } while ((t == null ? void 0 : t.size) === 0);
}, Pe = (n) => {
  for (let e; e = n._$AM; n = e) {
    let t = e._$AN;
    if (t === void 0) e._$AN = t = /* @__PURE__ */ new Set();
    else if (t.has(n)) break;
    t.add(n), ot(e);
  }
};
function nt(n) {
  this._$AN !== void 0 ? (F(this), this._$AM = n, Pe(this)) : this._$AM = n;
}
function st(n, e = !1, t = 0) {
  const i = this._$AH, r = this._$AN;
  if (r !== void 0 && r.size !== 0) if (e) if (Array.isArray(i)) for (let s = t; s < i.length; s++) B(i[s], !1), F(i[s]);
  else i != null && (B(i, !1), F(i));
  else B(this, n);
}
const ot = (n) => {
  n.type == tt.CHILD && (n._$AP ?? (n._$AP = st), n._$AQ ?? (n._$AQ = nt));
};
class at extends rt {
  constructor() {
    super(...arguments), this._$AN = void 0;
  }
  _$AT(e, t, i) {
    super._$AT(e, t, i), Pe(this), this.isConnected = e._$AU;
  }
  _$AO(e, t = !0) {
    var i, r;
    e !== this.isConnected && (this.isConnected = e, e ? (i = this.reconnected) == null || i.call(this) : (r = this.disconnected) == null || r.call(this)), t && (B(this, e), F(this));
  }
  setValue(e) {
    if (et(this._$Ct)) this._$Ct._$AI(e, this);
    else {
      const t = [...this._$Ct._$AH];
      t[this._$Ci] = e, this._$Ct._$AI(t, this, 0);
    }
  }
  disconnected() {
  }
  reconnected() {
  }
}
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const lt = () => new ct();
class ct {
}
const Y = /* @__PURE__ */ new WeakMap(), dt = it(class extends at {
  render(n) {
    return h;
  }
  update(n, [e]) {
    var i;
    const t = e !== this.G;
    return t && this.G !== void 0 && this.rt(void 0), (t || this.lt !== this.ct) && (this.G = e, this.ht = (i = n.options) == null ? void 0 : i.host, this.rt(this.ct = n.element)), h;
  }
  rt(n) {
    if (this.isConnected || (n = void 0), typeof this.G == "function") {
      const e = this.ht ?? globalThis;
      let t = Y.get(e);
      t === void 0 && (t = /* @__PURE__ */ new WeakMap(), Y.set(e, t)), t.get(this.G) !== void 0 && this.G.call(this.ht, void 0), t.set(this.G, n), n !== void 0 && this.G.call(this.ht, n);
    } else this.G.value = n;
  }
  get lt() {
    var n, e;
    return typeof this.G == "function" ? (n = Y.get(this.ht ?? globalThis)) == null ? void 0 : n.get(this.G) : (e = this.G) == null ? void 0 : e.value;
  }
  disconnected() {
    this.lt === this.ct && this.rt(void 0);
  }
  reconnected() {
    this.rt(this.ct);
  }
}), ht = (n, e, t) => Number.isInteger(n) ? `calc(((${e}px - (${Math.trunc(
  n
)} * ${t})) / ${n}) + (${t} / ${n}))` : `calc((${e}px - (${Math.trunc(
  n
)} * ${t})) / ${n})`, ut = 'html{font-size:6px}@font-face{font-family:Eduard Breuninger;font-style:normal;font-weight:400;font-display:swap;src:url(https://assets.prod.breuni.de/assets/eb-font/v0.0.4/EduardBreuninger-Regular.woff2) format("woff2")}@font-face{font-family:Eduard Breuninger;font-style:normal;font-weight:500;font-display:swap;src:url(https://assets.prod.breuni.de/assets/eb-font/v0.0.4/EduardBreuninger-Medium.woff2) format("woff2")}html,:root,:host{--nui-alabaster: rgba(250,248,247,1);--nui-backdrop-layer: rgba(26,26,26,.5);--nui-beyond-bronze: rgba(167,71,18,1);--nui-beyond-bronze-bg: rgba(247,238,232,1);--nui-breuninger-orange: rgba(243,138,1,1);--nui-breuninger-orange-bg: rgba(255,244,240,1);--nui-breuninger-red: rgba(186,12,47,1);--nui-breuninger-red-hover: rgba(186,12,47,1);--nui-business: rgba(108,103,101,1);--nui-coal: rgba(26,26,26,1);--nui-concrete: rgba(244,242,242,1);--nui-error: rgba(213,0,0,1);--nui-error-bg: rgba(255,240,240,1);--nui-ghost: rgba(255,255,255,0);--nui-home-and-living: rgba(186,180,180,1);--nui-marketplace: rgba(16,53,185,1);--nui-red-button-hover: rgba(140,9,36,1);--nui-sale: rgba(255,237,0,1);--nui-smoke: rgba(146,141,139,1);--nui-snow: rgba(255,255,255,1);--nui-success: rgba(65,117,5,1);--nui-success-bg: rgba(238,248,228,1);--nui-sustainability: rgba(224,218,210,1);--nui-space-1: 1rem;--nui-space-10: 10rem;--nui-space-11: 11rem;--nui-space-12: 12rem;--nui-space-13: 13rem;--nui-space-14: 14rem;--nui-space-15: 15rem;--nui-space-16: 16rem;--nui-space-17: 17rem;--nui-space-2: 2rem;--nui-space-2xs: 1rem;--nui-space-3: 3rem;--nui-space-4: 4rem;--nui-space-5: 5rem;--nui-space-6: 6rem;--nui-space-7: 7rem;--nui-space-8: 8rem;--nui-space-9: 9rem;--nui-space-ho-2xs: 1rem;--nui-space-ho-l: 5rem;--nui-space-ho-m: 4rem;--nui-space-ho-s: 3rem;--nui-space-ho-xl: 7rem;--nui-space-ho-xs: 2rem;--nui-space-l: 5rem;--nui-space-m: 4rem;--nui-space-s: 3rem;--nui-space-ve-2xl: 15rem;--nui-space-ve-2xs: 3rem;--nui-space-ve-3xl: 17rem;--nui-space-ve-3xs: 2rem;--nui-space-ve-4xs: 1rem;--nui-space-ve-l: 10rem;--nui-space-ve-m: 7rem;--nui-space-ve-s: 5rem;--nui-space-ve-xl: 12rem;--nui-space-ve-xs: 4rem;--nui-space-xl: 7rem;--nui-space-xs: 2rem;--nui-brand-heading-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-brand-heading-bold-font-size: 2.667rem;--nui-brand-heading-bold-font-weight: 500;--nui-brand-heading-bold-letter-spacing: .133rem;--nui-brand-heading-bold-line-height: 5rem;--nui-brand-heading-bold-text-decoration: none;--nui-brand-heading-bold-text-transform: inherit;--nui-brand-heading-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-brand-heading-font-size: 2.667rem;--nui-brand-heading-font-weight: 400;--nui-brand-heading-large-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-brand-heading-large-bold-font-size: 3.333rem;--nui-brand-heading-large-bold-font-weight: 500;--nui-brand-heading-large-bold-letter-spacing: .133rem;--nui-brand-heading-large-bold-line-height: 6rem;--nui-brand-heading-large-bold-text-decoration: none;--nui-brand-heading-large-bold-text-transform: inherit;--nui-brand-heading-large-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-brand-heading-large-font-size: 3.333rem;--nui-brand-heading-large-font-weight: 400;--nui-brand-heading-large-letter-spacing: .133rem;--nui-brand-heading-large-line-height: 6rem;--nui-brand-heading-large-text-decoration: none;--nui-brand-heading-large-text-transform: inherit;--nui-brand-heading-letter-spacing: .133rem;--nui-brand-heading-line-height: 5rem;--nui-brand-heading-small-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-brand-heading-small-font-size: 2.333rem;--nui-brand-heading-small-font-weight: 400;--nui-brand-heading-small-letter-spacing: .133rem;--nui-brand-heading-small-line-height: 3.667rem;--nui-brand-heading-small-text-decoration: none;--nui-brand-heading-small-text-transform: inherit;--nui-brand-heading-text-decoration: none;--nui-brand-heading-text-transform: inherit;--nui-button-copy-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-button-copy-font-size: 2.333rem;--nui-button-copy-font-weight: 500;--nui-button-copy-letter-spacing: .133rem;--nui-button-copy-line-height: 3.667rem;--nui-button-copy-text-decoration: none;--nui-button-copy-text-transform: uppercase;--nui-copy-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-bold-font-size: 2.667rem;--nui-copy-bold-font-weight: 500;--nui-copy-bold-letter-spacing: .133rem;--nui-copy-bold-line-height: 3.667rem;--nui-copy-bold-text-decoration: none;--nui-copy-bold-text-transform: inherit;--nui-copy-extra-small-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-extra-small-bold-font-size: 2rem;--nui-copy-extra-small-bold-font-weight: 500;--nui-copy-extra-small-bold-letter-spacing: .167rem;--nui-copy-extra-small-bold-line-height: 3.167rem;--nui-copy-extra-small-bold-text-decoration: none;--nui-copy-extra-small-bold-text-transform: inherit;--nui-copy-extra-small-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-extra-small-font-size: 2rem;--nui-copy-extra-small-font-weight: 400;--nui-copy-extra-small-letter-spacing: .167rem;--nui-copy-extra-small-line-height: 3.167rem;--nui-copy-extra-small-text-decoration: none;--nui-copy-extra-small-text-transform: inherit;--nui-copy-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-font-size: 2.667rem;--nui-copy-font-weight: 400;--nui-copy-large-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-large-bold-font-size: 4rem;--nui-copy-large-bold-font-weight: 500;--nui-copy-large-bold-letter-spacing: .133rem;--nui-copy-large-bold-line-height: 5rem;--nui-copy-large-bold-text-decoration: none;--nui-copy-large-bold-text-transform: inherit;--nui-copy-letter-spacing: .133rem;--nui-copy-line-height: 3.667rem;--nui-copy-medium-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-medium-bold-font-size: 3.333rem;--nui-copy-medium-bold-font-weight: 500;--nui-copy-medium-bold-letter-spacing: .133rem;--nui-copy-medium-bold-line-height: 4.333rem;--nui-copy-medium-bold-text-decoration: none;--nui-copy-medium-bold-text-transform: inherit;--nui-copy-medium-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-medium-font-size: 3.333rem;--nui-copy-medium-font-weight: 400;--nui-copy-medium-letter-spacing: .133rem;--nui-copy-medium-line-height: 4.333rem;--nui-copy-medium-text-decoration: none;--nui-copy-medium-text-transform: inherit;--nui-copy-small-bold-capitals-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-small-bold-capitals-font-size: 2.333rem;--nui-copy-small-bold-capitals-font-weight: 500;--nui-copy-small-bold-capitals-letter-spacing: .167rem;--nui-copy-small-bold-capitals-line-height: 3.167rem;--nui-copy-small-bold-capitals-text-decoration: none;--nui-copy-small-bold-capitals-text-transform: uppercase;--nui-copy-small-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-small-bold-font-size: 2.333rem;--nui-copy-small-bold-font-weight: 500;--nui-copy-small-bold-letter-spacing: .167rem;--nui-copy-small-bold-line-height: 3.167rem;--nui-copy-small-bold-text-decoration: none;--nui-copy-small-bold-text-transform: inherit;--nui-copy-small-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-copy-small-font-size: 2.333rem;--nui-copy-small-font-weight: 400;--nui-copy-small-letter-spacing: .167rem;--nui-copy-small-line-height: 3.167rem;--nui-copy-small-text-decoration: none;--nui-copy-small-text-transform: inherit;--nui-copy-text-decoration: none;--nui-copy-text-transform: inherit;--nui-heading-extralarge-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-heading-extralarge-font-size: 5rem;--nui-heading-extralarge-font-weight: 500;--nui-heading-extralarge-letter-spacing: .133rem;--nui-heading-extralarge-line-height: 6rem;--nui-heading-extralarge-text-decoration: none;--nui-heading-extralarge-text-transform: uppercase;--nui-heading-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-heading-font-size: 2.667rem;--nui-heading-font-weight: 500;--nui-heading-large-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-heading-large-font-size: 4rem;--nui-heading-large-font-weight: 500;--nui-heading-large-letter-spacing: .133rem;--nui-heading-large-line-height: 5rem;--nui-heading-large-text-decoration: none;--nui-heading-large-text-transform: uppercase;--nui-heading-letter-spacing: .133rem;--nui-heading-line-height: 3.667rem;--nui-heading-medium-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-heading-medium-font-size: 3.333rem;--nui-heading-medium-font-weight: 500;--nui-heading-medium-letter-spacing: .133rem;--nui-heading-medium-line-height: 4.333rem;--nui-heading-medium-text-decoration: none;--nui-heading-medium-text-transform: uppercase;--nui-heading-text-decoration: none;--nui-heading-text-transform: uppercase;--nui-link-default-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-link-default-font-size: 2.667rem;--nui-link-default-font-weight: 400;--nui-link-default-letter-spacing: .133rem;--nui-link-default-line-height: 3.667rem;--nui-link-default-text-decoration: underline;--nui-link-default-text-transform: inherit;--nui-link-extra-small-default-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-link-extra-small-default-font-size: 2rem;--nui-link-extra-small-default-font-weight: 400;--nui-link-extra-small-default-letter-spacing: .167rem;--nui-link-extra-small-default-line-height: 3.167rem;--nui-link-extra-small-default-text-decoration: underline;--nui-link-extra-small-default-text-transform: inherit;--nui-link-small-default-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-link-small-default-font-size: 2.333rem;--nui-link-small-default-font-weight: 400;--nui-link-small-default-letter-spacing: .167rem;--nui-link-small-default-line-height: 3.167rem;--nui-link-small-default-text-decoration: underline;--nui-link-small-default-text-transform: inherit;--nui-prose-copy-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-prose-copy-bold-font-size: 2.667rem;--nui-prose-copy-bold-font-weight: 500;--nui-prose-copy-bold-letter-spacing: .133rem;--nui-prose-copy-bold-line-height: 5rem;--nui-prose-copy-bold-text-decoration: none;--nui-prose-copy-bold-text-transform: inherit;--nui-prose-copy-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-prose-copy-font-size: 2.667rem;--nui-prose-copy-font-weight: 400;--nui-prose-copy-letter-spacing: .133rem;--nui-prose-copy-line-height: 5rem;--nui-prose-copy-small-bold-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-prose-copy-small-bold-font-size: 2.333rem;--nui-prose-copy-small-bold-font-weight: 500;--nui-prose-copy-small-bold-letter-spacing: .167rem;--nui-prose-copy-small-bold-line-height: 5rem;--nui-prose-copy-small-bold-text-decoration: none;--nui-prose-copy-small-bold-text-transform: inherit;--nui-prose-copy-small-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-prose-copy-small-font-size: 2.333rem;--nui-prose-copy-small-font-weight: 400;--nui-prose-copy-small-letter-spacing: .167rem;--nui-prose-copy-small-line-height: 4rem;--nui-prose-copy-small-text-decoration: none;--nui-prose-copy-small-text-transform: inherit;--nui-prose-copy-text-decoration: none;--nui-prose-copy-text-transform: inherit;--nui-prose-heading-extralarge-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-prose-heading-extralarge-font-size: 5rem;--nui-prose-heading-extralarge-font-weight: 500;--nui-prose-heading-extralarge-letter-spacing: .133rem;--nui-prose-heading-extralarge-line-height: 8rem;--nui-prose-heading-extralarge-text-decoration: none;--nui-prose-heading-extralarge-text-transform: uppercase;--nui-prose-heading-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-prose-heading-font-size: 3.333rem;--nui-prose-heading-font-weight: 400;--nui-prose-heading-large-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-prose-heading-large-font-size: 4rem;--nui-prose-heading-large-font-weight: 500;--nui-prose-heading-large-letter-spacing: .133rem;--nui-prose-heading-large-line-height: 7rem;--nui-prose-heading-large-text-decoration: none;--nui-prose-heading-large-text-transform: uppercase;--nui-prose-heading-letter-spacing: .133rem;--nui-prose-heading-line-height: 6rem;--nui-prose-heading-medium-font-family: "Eduard Breuninger",Helvetica,Arial,sans-serif;--nui-prose-heading-medium-font-size: 3.333rem;--nui-prose-heading-medium-font-weight: 500;--nui-prose-heading-medium-letter-spacing: .133rem;--nui-prose-heading-medium-line-height: 6rem;--nui-prose-heading-medium-text-decoration: none;--nui-prose-heading-medium-text-transform: uppercase;--nui-prose-heading-text-decoration: none;--nui-prose-heading-text-transform: inherit;--nui-breakpoint-2xl: 1360px;--nui-breakpoint-2xs: 320px;--nui-breakpoint-3xl: 1660px;--nui-breakpoint-full-hd: 1920px;--nui-breakpoint-l: 960px;--nui-breakpoint-m: 760px;--nui-breakpoint-s: 560px;--nui-breakpoint-xl: 1160px;--nui-breakpoint-xs: 360px;--nui-elevation-above-header: 45;--nui-elevation-above-menu: 55;--nui-elevation-above-overlay: 85;--nui-elevation-below-header: 35;--nui-elevation-below-menu: 45;--nui-elevation-below-overlay: 55;--nui-elevation-header: 40;--nui-elevation-layer: 90;--nui-elevation-menu: 50;--nui-elevation-offcanvas: 100;--nui-elevation-overlay: 80}*,:before,:after{--tw-border-spacing-x: 0;--tw-border-spacing-y: 0;--tw-translate-x: 0;--tw-translate-y: 0;--tw-rotate: 0;--tw-skew-x: 0;--tw-skew-y: 0;--tw-scale-x: 1;--tw-scale-y: 1;--tw-pan-x: ;--tw-pan-y: ;--tw-pinch-zoom: ;--tw-scroll-snap-strictness: proximity;--tw-gradient-from-position: ;--tw-gradient-via-position: ;--tw-gradient-to-position: ;--tw-ordinal: ;--tw-slashed-zero: ;--tw-numeric-figure: ;--tw-numeric-spacing: ;--tw-numeric-fraction: ;--tw-ring-inset: ;--tw-ring-offset-width: 0px;--tw-ring-offset-color: #fff;--tw-ring-color: rgb(59 130 246 / .5);--tw-ring-offset-shadow: 0 0 #0000;--tw-ring-shadow: 0 0 #0000;--tw-shadow: 0 0 #0000;--tw-shadow-colored: 0 0 #0000;--tw-blur: ;--tw-brightness: ;--tw-contrast: ;--tw-grayscale: ;--tw-hue-rotate: ;--tw-invert: ;--tw-saturate: ;--tw-sepia: ;--tw-drop-shadow: ;--tw-backdrop-blur: ;--tw-backdrop-brightness: ;--tw-backdrop-contrast: ;--tw-backdrop-grayscale: ;--tw-backdrop-hue-rotate: ;--tw-backdrop-invert: ;--tw-backdrop-opacity: ;--tw-backdrop-saturate: ;--tw-backdrop-sepia: ;--tw-contain-size: ;--tw-contain-layout: ;--tw-contain-paint: ;--tw-contain-style: }::backdrop{--tw-border-spacing-x: 0;--tw-border-spacing-y: 0;--tw-translate-x: 0;--tw-translate-y: 0;--tw-rotate: 0;--tw-skew-x: 0;--tw-skew-y: 0;--tw-scale-x: 1;--tw-scale-y: 1;--tw-pan-x: ;--tw-pan-y: ;--tw-pinch-zoom: ;--tw-scroll-snap-strictness: proximity;--tw-gradient-from-position: ;--tw-gradient-via-position: ;--tw-gradient-to-position: ;--tw-ordinal: ;--tw-slashed-zero: ;--tw-numeric-figure: ;--tw-numeric-spacing: ;--tw-numeric-fraction: ;--tw-ring-inset: ;--tw-ring-offset-width: 0px;--tw-ring-offset-color: #fff;--tw-ring-color: rgb(59 130 246 / .5);--tw-ring-offset-shadow: 0 0 #0000;--tw-ring-shadow: 0 0 #0000;--tw-shadow: 0 0 #0000;--tw-shadow-colored: 0 0 #0000;--tw-blur: ;--tw-brightness: ;--tw-contrast: ;--tw-grayscale: ;--tw-hue-rotate: ;--tw-invert: ;--tw-saturate: ;--tw-sepia: ;--tw-drop-shadow: ;--tw-backdrop-blur: ;--tw-backdrop-brightness: ;--tw-backdrop-contrast: ;--tw-backdrop-grayscale: ;--tw-backdrop-hue-rotate: ;--tw-backdrop-invert: ;--tw-backdrop-opacity: ;--tw-backdrop-saturate: ;--tw-backdrop-sepia: ;--tw-contain-size: ;--tw-contain-layout: ;--tw-contain-paint: ;--tw-contain-style: }*,:before,:after{box-sizing:border-box;border-width:0;border-style:solid;border-color:#e5e7eb}:before,:after{--tw-content: ""}html,:host{line-height:1.5;-webkit-text-size-adjust:100%;-moz-tab-size:4;tab-size:4;font-family:Eduard Breuninger,ui-sans-serif,system-ui,sans-serif,"Apple Color Emoji","Segoe UI Emoji",Segoe UI Symbol,"Noto Color Emoji";font-feature-settings:normal;font-variation-settings:normal;-webkit-tap-highlight-color:transparent}body{margin:0;line-height:inherit}hr{height:0;color:inherit;border-top-width:1px}abbr:where([title]){text-decoration:underline dotted}h1,h2,h3,h4,h5,h6{font-size:inherit;font-weight:inherit}a{color:inherit;text-decoration:inherit}b,strong{font-weight:bolder}code,kbd,samp,pre{font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,Liberation Mono,Courier New,monospace;font-feature-settings:normal;font-variation-settings:normal;font-size:1em}small{font-size:80%}sub,sup{font-size:75%;line-height:0;position:relative;vertical-align:baseline}sub{bottom:-.25em}sup{top:-.5em}table{text-indent:0;border-color:inherit;border-collapse:collapse}button,input,optgroup,select,textarea{font-family:inherit;font-feature-settings:inherit;font-variation-settings:inherit;font-size:100%;font-weight:inherit;line-height:inherit;letter-spacing:inherit;color:inherit;margin:0;padding:0}button,select{text-transform:none}button,input:where([type=button]),input:where([type=reset]),input:where([type=submit]){-webkit-appearance:button;background-color:transparent;background-image:none}:-moz-focusring{outline:auto}:-moz-ui-invalid{box-shadow:none}progress{vertical-align:baseline}::-webkit-inner-spin-button,::-webkit-outer-spin-button{height:auto}[type=search]{-webkit-appearance:textfield;outline-offset:-2px}::-webkit-search-decoration{-webkit-appearance:none}::-webkit-file-upload-button{-webkit-appearance:button;font:inherit}summary{display:list-item}blockquote,dl,dd,h1,h2,h3,h4,h5,h6,hr,figure,p,pre{margin:0}fieldset{margin:0;padding:0}legend{padding:0}ol,ul,menu{list-style:none;margin:0;padding:0}dialog{padding:0}textarea{resize:vertical}input::placeholder,textarea::placeholder{opacity:1;color:#9ca3af}button,[role=button]{cursor:pointer}:disabled{cursor:default}img,svg,video,canvas,audio,iframe,embed,object{display:block;vertical-align:middle}img,video{max-width:100%;height:auto}[hidden]:where(:not([hidden=until-found])){display:none}.container{width:100%}@media (min-width: 320px){.container{max-width:320px}}@media (min-width: 360px){.container{max-width:360px}}@media (min-width: 560px){.container{max-width:560px}}@media (min-width: 640px){.container{max-width:640px}}@media (min-width: 760px){.container{max-width:760px}}@media (min-width: 768px){.container{max-width:768px}}@media (min-width: 960px){.container{max-width:960px}}@media (min-width: 1024px){.container{max-width:1024px}}@media (min-width: 1160px){.container{max-width:1160px}}@media (min-width: 1280px){.container{max-width:1280px}}@media (min-width: 1360px){.container{max-width:1360px}}@media (min-width: 1536px){.container{max-width:1536px}}@media (min-width: 1660px){.container{max-width:1660px}}@media (min-width: 1920px){.container{max-width:1920px}}.visible{visibility:visible}.static{position:static}.block{display:block}.inline{display:inline}.grid{display:grid}.contents{display:contents}.resize{resize:both}.snap-x{scroll-snap-type:x var(--tw-scroll-snap-strictness)}.snap-mandatory{--tw-scroll-snap-strictness: mandatory}.auto-cols-max{grid-auto-columns:max-content}.grid-flow-col{grid-auto-flow:column}.overflow-x-scroll{overflow-x:scroll}.filter{filter:var(--tw-blur) var(--tw-brightness) var(--tw-contrast) var(--tw-grayscale) var(--tw-hue-rotate) var(--tw-invert) var(--tw-saturate) var(--tw-sepia) var(--tw-drop-shadow)}', we = { 1: "1rem", 2: "2rem", 3: "3rem", 4: "4rem", 5: "5rem", 6: "6rem", 7: "7rem", 8: "8rem", 9: "9rem", 10: "10rem", 11: "11rem", 12: "12rem", 13: "13rem", 14: "14rem", 15: "15rem", 16: "16rem", 17: "17rem", "2xs": "1rem", "ho-2xs": "1rem", "ho-l": "5rem", "ho-m": "4rem", "ho-s": "3rem", "ho-xl": "7rem", "ho-xs": "2rem", l: "5rem", m: "4rem", s: "3rem", "ve-2xl": "15rem", "ve-2xs": "3rem", "ve-3xl": "17rem", "ve-3xs": "2rem", "ve-4xs": "1rem", "ve-l": "10rem", "ve-m": "7rem", "ve-s": "5rem", "ve-xl": "12rem", "ve-xs": "4rem", xl: "7rem", xs: "2rem" };
var pt = Object.defineProperty, m = (n, e, t, i) => {
  for (var r = void 0, s = n.length - 1, o; s >= 0; s--)
    (o = n[s]) && (r = o(e, t, r) || r);
  return r && pt(e, t, r), r;
};
const mt = 3.5, ft = 10, gt = 0.5, ce = class ce extends x {
  constructor() {
    super(), this.scrollSnap = !1, this.spacing = "1", this.noAriaLivePolite = !1, this.containerRef = lt(), this.gap = "1rem", this.pos = { top: 0, left: 0, x: 0 }, this.resizeThreshold = null, this.linkNavigationThreshold = null, this.linkNavigationLocked = !1, this.intersectionObserver = null, this.lastSlideIntersectionObserver = null, this.firstSlideIntersectionObserver = null, this.reinitIntersectionObserver = null, this.debouncedResizeListener = () => {
      this.resizeThreshold && clearTimeout(this.resizeThreshold), this.resizeThreshold = setTimeout(this.setSlideWidth, ft);
    }, this.setSlideWidth = () => {
      if (this.reinitIntersectionObserver && (this.reinitIntersectionObserver.disconnect(), this.reinitIntersectionObserver = null), this.offsetWidth === 0) {
        this.reinitIntersectionObserver = new IntersectionObserver((e) => {
          var t, i;
          (t = e[0]) != null && t.isIntersecting && ((i = this.intersectionObserver) == null || i.disconnect(), this.reinitIntersectionObserver = null, this.setSlideWidth());
        }), this.reinitIntersectionObserver.observe(this);
        return;
      }
      this.intersectionObserver && this.intersectionObserver.disconnect(), this.initIntersectionObserver(), requestAnimationFrame(() => {
        var r;
        const e = this.numSlidesVisible(), t = this.offsetWidth, i = ht(e, t, this.gap);
        (r = this.slottedItems) == null || r.forEach((s) => {
          s.style.width = i, this.intersectionObserver && this.intersectionObserver.observe(s);
        });
      });
    }, this.setSlidesGap = () => {
      var e;
      (e = this.containerRef.value) == null || e.style.setProperty("column-gap", this.gap);
    }, this.slidesGap = () => {
      const t = getComputedStyle(this.grid).getPropertyValue("grid-column-gap");
      return isNaN(parseFloat(t)) ? 6 : parseFloat(t);
    }, this.next = () => {
      requestAnimationFrame(() => {
        const e = this.numSlidesVisible(), t = this.slottedItems.filter(
          (r) => r.getAttribute("data-visible") === "true"
        );
        if (!Array.isArray(t) || t.length === 0)
          return;
        const i = t.reverse()[0];
        if (e === 1) {
          const r = i == null ? void 0 : i.getAttribute("data-slide-index"), s = r ? this.slottedItems.find((o) => o.getAttribute("data-slide-index") === (parseInt(r) + 1).toString()) : null;
          s && this.scrollToElement({
            scrollContainer: this.container,
            element: s,
            direction: "next"
          });
        } else
          this.scrollToElement({
            scrollContainer: this.container,
            element: i,
            direction: "next"
          });
      });
    }, this.prev = () => {
      requestAnimationFrame(() => {
        const e = this.slottedItems.filter((t) => t.getAttribute("data-visible") === "true")[0];
        this.scrollToElement({
          scrollContainer: this.container,
          element: e,
          direction: "prev"
        });
      });
    }, this.debouncedResizeListener = this.debouncedResizeListener.bind(this);
  }
  numSlidesVisible() {
    const e = getComputedStyle(this).getPropertyValue("--num-slides"), t = parseFloat(e);
    return isNaN(t) ? mt : t;
  }
  connectedCallback() {
    super.connectedCallback(), this.gap = we[this.spacing], window.addEventListener("resize", this.debouncedResizeListener);
  }
  disconnectedCallback() {
    var e, t, i, r;
    super.disconnectedCallback(), window.removeEventListener("resize", this.debouncedResizeListener), (e = this.intersectionObserver) == null || e.disconnect(), (t = this.firstSlideIntersectionObserver) == null || t.disconnect(), (i = this.lastSlideIntersectionObserver) == null || i.disconnect(), (r = this.reinitIntersectionObserver) == null || r.disconnect();
  }
  attributeChangedCallback(e, t, i) {
    var r, s, o, l;
    super.attributeChangedCallback(e, t, i), e === "spacing" && (this.gap = we[i], this.setSlidesGap(), this.setSlideWidth()), e === "scroll-snap" && (i ? ((r = this.container) == null || r.style.setProperty(
      "scroll-snap-type",
      "x mandatory"
    ), (s = this.slottedItems) == null || s.forEach((a) => {
      a.style.setProperty("scroll-snap-align", "start");
    })) : ((o = this.container) == null || o.style.removeProperty("scroll-snap-type"), (l = this.slottedItems) == null || l.forEach((a) => {
      a.style.removeProperty("scroll-snap-align");
    })));
  }
  refresh() {
    this.setSlideWidth();
  }
  initLastSlideIntersectionObserver() {
    const e = {
      root: this.container,
      rootMargin: "0px",
      threshold: [0.99]
    };
    this.lastSlideIntersectionObserver = new IntersectionObserver(
      (t) => {
        t.forEach((i) => {
          i.isIntersecting ? this.dispatchEvent(
            new CustomEvent("carousel:last-slide-visible", {
              bubbles: !0
            })
          ) : this.dispatchEvent(
            new CustomEvent("carousel:last-slide-not-visible", {
              bubbles: !0
            })
          );
        });
      },
      e
    );
  }
  initFirstSlideIntersectionObserver() {
    const e = {
      root: this.container,
      rootMargin: "0px",
      threshold: [0.99]
    };
    this.firstSlideIntersectionObserver = new IntersectionObserver(
      (t) => {
        t.forEach((i) => {
          i.isIntersecting ? this.dispatchEvent(
            new CustomEvent("carousel:first-slide-visible", {
              bubbles: !0
            })
          ) : this.dispatchEvent(
            new CustomEvent(
              "carousel:first-slide-not-visible",
              {
                bubbles: !0
              }
            )
          );
        });
      },
      e
    );
  }
  initIntersectionObserver() {
    const e = {
      root: this.container,
      rootMargin: "0px",
      threshold: [gt - 0.01]
    };
    this.intersectionObserver = new IntersectionObserver((t) => {
      t.forEach((i) => {
        i.isIntersecting ? i.target.setAttribute("data-visible", "true") : i.target.removeAttribute("data-visible");
      });
    }, e);
  }
  firstUpdated() {
    var e, t, i;
    this.setSlidesGap(), this.setSlideWidth(), this.initIntersectionObserver(), this.initLastSlideIntersectionObserver(), this.initFirstSlideIntersectionObserver(), this.scrollSnap && this.container.style.setProperty("scroll-snap-type", "x mandatory"), this.slottedItems[0] && ((e = this.firstSlideIntersectionObserver) == null || e.observe(this.slottedItems[0])), this.slottedItems[this.slottedItems.length - 1] && ((t = this.lastSlideIntersectionObserver) == null || t.observe(
      this.slottedItems[this.slottedItems.length - 1]
    )), (i = this.slottedItems) == null || i.forEach((r, s) => {
      this.intersectionObserver && this.intersectionObserver.observe(r), requestAnimationFrame(() => {
        r.setAttribute("data-is-slide", "true"), r.setAttribute("draggable", "false"), r.setAttribute("data-slide-index", "" + s), r.style.setProperty("will-change", "scroll-position"), this.scrollSnap && r.style.setProperty("scroll-snap-align", "start"), r.setAttribute(
          "aria-label",
          `${s + 1} of ${this.slottedItems.length}`
        ), r.setAttribute("aria-roledescription", "item");
      }), r.querySelectorAll("img").forEach((c) => {
        c == null || c.setAttribute("draggable", "false");
      }), r.querySelectorAll("video").forEach((c) => {
        c == null || c.setAttribute("draggable", "false");
      }), r.querySelectorAll("a").forEach((c) => {
        c == null || c.setAttribute("draggable", "false");
      });
    });
  }
  /**
   * Scrolls the carousel a given element in a given direction. The direction is either 'next' or 'prev'.
   * @param scrollContainer
   * @param element
   * @param direction
   * @private
   */
  scrollToElement({
    scrollContainer: e,
    element: t,
    direction: i = "next"
  }) {
    if (t === null) return;
    const r = e.getBoundingClientRect(), s = t.getBoundingClientRect();
    let o = 0;
    if (i === "next")
      o = s.left - r.left;
    else {
      const c = this.numSlidesVisible(), u = c - Math.trunc(c), d = s.width * u, g = this.slidesGap();
      o = -(e.clientWidth + d + g);
    }
    const l = e.scrollWidth - e.clientWidth, a = Math.min(
      Math.max(e.scrollLeft + o, 0),
      l
    );
    e.scrollTo({
      left: a,
      behavior: "smooth"
    });
  }
  render() {
    return V`
            <div
                class="grid grid-auto-rows"
                aria-roledescription="carousel"
                tabindex="-1"
            >
                <div
                    class="carousel-container w-100 overflow-x-scroll snap-x snap-mandatory"
                >
                    <div
                        class="carousel-grid grid auto-cols-max grid-flow-col"
                        role="group"
                        aria-label="Carousel"
                        ?aria-live=${this.noAriaLivePolite ? void 0 : "polite"}
                        data-testid="carousel-grid"
                        ${dt(this.containerRef)}
                    >
                        <slot></slot>
                    </div>
                </div>
            </div>
        `;
  }
};
ce.styles = ie`
        ${Ae(ut)}
        :host {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            max-height: 100% !important;
        }
        .carousel-container::-webkit-scrollbar {
            display: none;
        }
        .carousel-container {
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */
        }
        .carousel-container {
            overflow: auto;
            user-select: none;
        }
    `;
let p = ce;
m([
  Oe(".carousel-container")
], p.prototype, "container");
m([
  Oe(".carousel-grid")
], p.prototype, "grid");
m([
  f({
    type: Boolean,
    reflect: !0,
    attribute: "scroll-snap"
  })
], p.prototype, "scrollSnap");
m([
  f({
    type: String,
    reflect: !0,
    attribute: "spacing"
  })
], p.prototype, "spacing");
m([
  f({
    type: Boolean,
    reflect: !0,
    attribute: "no-aria-live-polite"
  })
], p.prototype, "noAriaLivePolite");
m([
  D({ selector: "*", flatten: !0, slot: "" })
], p.prototype, "slottedItems");
m([
  b()
], p.prototype, "gap");
m([
  b()
], p.prototype, "pos");
m([
  b()
], p.prototype, "resizeThreshold");
m([
  b()
], p.prototype, "linkNavigationThreshold");
m([
  b()
], p.prototype, "linkNavigationLocked");
m([
  b()
], p.prototype, "intersectionObserver");
m([
  b()
], p.prototype, "lastSlideIntersectionObserver");
m([
  b()
], p.prototype, "firstSlideIntersectionObserver");
m([
  b()
], p.prototype, "reinitIntersectionObserver");
export {
  p as Carousel,
  P as CarouselContainer,
  R as CarouselNavigationNext,
  U as CarouselNavigationPrev
};
